import React from "react";

import {
    CarouselMedia,
    EmbeddedIframeMedia,
    ImageMedia,
    SnapshotChartMedia,
    VideoMedia,
    DEFAULT_MEDIA_PRIORITY,
    getWinningMediaFormat,
    MediaFormat,
    type MediaParentProps,
} from "./";
import { isVideoMediaFormat } from "./utils/internal";

/**
 * Example interface for consuming component
 *  <Media
 *    source={item}
 *    priority={MODULE_DEFINED_PRIORITY_ARRAY as MediaFormat[]}
 *    config={MODULE_DEFINED_COMPONENT_CONFIG as MediaConfig
 *  />
 */
export function Media(props: MediaParentProps) {
    const { source, priority = DEFAULT_MEDIA_PRIORITY, config = {} } = props;

    // Calculate the winning Media Format based
    // on source and priority
    const winningMediaFormat = getWinningMediaFormat(source, priority);

    // Return null if we have no winner
    if (
        winningMediaFormat === MediaFormat.missingMedia ||
        winningMediaFormat === MediaFormat.optOutMedia
    ) {
        return null;
    }

    // use winningMediaFormat to return the correct
    // media child component.

    if (winningMediaFormat === MediaFormat.carousel) {
        return (
            <CarouselMedia
                source={source}
                format={winningMediaFormat}
                config={config}
            />
        );
    }

    if (winningMediaFormat === MediaFormat.embeddedIframe) {
        return (
            <EmbeddedIframeMedia
                source={source}
                format={winningMediaFormat}
                config={config}
            />
        );
    }

    if (winningMediaFormat === MediaFormat.snapshotChart) {
        return (
            <SnapshotChartMedia
                source={source}
                format={winningMediaFormat}
                config={config}
            />
        );
    }

    if (isVideoMediaFormat(winningMediaFormat)) {
        return (
            <VideoMedia
                source={source}
                format={winningMediaFormat}
                config={config}
            />
        );
    }

    return (
        <ImageMedia
            source={source}
            format={winningMediaFormat}
            config={config}
        />
    );
}

export const MemoizedMedia = React.memo(Media);
